import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { Bar } from 'react-chartjs-2'

import { Text } from '../../common/designs'
import { ChartData } from '../../common/types/common'

interface Props {
  title: string
  data: ChartData | undefined
  expectedAgentAmount: number | null
}

const ConnectivityGraph = ({ title, data, expectedAgentAmount }: Props) => {
  const annotationColor = 'hsla(239, 84%, 67%, 1)'
  const computerDataPoints = data?.datasets[0].data ?? [0]
  const { minComputers, avgComputers, maxComputers } = {
    minComputers: Math.min(...computerDataPoints),
    avgComputers: computerDataPoints.reduce((a, b) => a + b, 0) / computerDataPoints.length,
    maxComputers: Math.max(...computerDataPoints),
  }

  return (
    <div className='rounded-md bg-white p-4 shadow-md'>
      <div className='flex justify-between'>
        <div className='mb-2 flex flex-col gap-4'>
          <Text>{title}</Text>

          {avgComputers === 0 ? (
            <div className='flex items-center gap-2 rounded-md border border-red-300 bg-red-50 px-1.5 py-1'>
              <ExclamationTriangleIcon className='size-5 text-red-600' />

              <Text size='xs' color='error' weight={500}>
                No computers have been detected!
              </Text>
            </div>
          ) : null}
        </div>

        {computerDataPoints.length ? (
          <>
            <div className='flex gap-8'>
              <Text className='mb-2' size='s'>
                Minimum: {minComputers}
              </Text>
              <Text className='mb-2' size='s'>
                Average: {Math.round(avgComputers * 10) / 10}
              </Text>
              <Text className='mb-2' size='s'>
                Maximum: {maxComputers}
              </Text>
            </div>
          </>
        ) : (
          <Text className='mb-2' size='s'>
            This team does not have any computers connected yet
          </Text>
        )}
      </div>

      {data && computerDataPoints.length ? (
        <Bar
          data={data}
          options={{
            scales: {
              y: {
                ticks: {
                  font: {
                    family: 'Inter',
                  },
                  stepSize: 1,
                },
                suggestedMax:
                  // Scale the graph by the biggest value found (either max computer or expected agent amount)
                  maxComputers > (expectedAgentAmount ?? 0)
                    ? maxComputers + 1
                    : (expectedAgentAmount ?? 0) + 1,
              },
            },
            plugins: {
              legend: {
                display: false,
              },
              annotation: {
                annotations: expectedAgentAmount
                  ? {
                      line1: {
                        type: 'line',
                        yMin: expectedAgentAmount,
                        yMax: expectedAgentAmount,
                        borderColor: annotationColor,
                        borderWidth: 1,
                        label: {
                          content: 'Expected computer amount',
                          backgroundColor: annotationColor,
                          display: true,
                        },
                      },
                    }
                  : {},
              },
            },
          }}
          height='60px'
        />
      ) : null}
    </div>
  )
}

export default ConnectivityGraph

